import { AsyncSelect, GroupType, OptionType } from '@atlaskit/select';
import React, { useCallback, useMemo, useState } from 'react';

import { TogglProjectExtended, TogglWorkspace } from '../types/toggl-api';
import { groupProjectsByWorkspace, groupWorkspaces } from '../utils/utils';

interface SelectProps {
  isDisabled: boolean;
  mappedTogglProjectId: number | undefined;
  togglProjects: TogglProjectExtended[];
  workspaces: TogglWorkspace[];
  onProjectSelected: (togglProjectId?: number) => Promise<void>;
}

export function TogglProjectSelect({
  isDisabled,
  mappedTogglProjectId,
  togglProjects,
  workspaces,
  onProjectSelected,
}: SelectProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const getFilteredOptions = useCallback(
    (input: string): GroupType<OptionType>[] => {
      const projects = togglProjects
        .filter((p) =>
          p.displayName.toLowerCase().includes(input.toLowerCase())
        )
        .slice(0, 50);
      const groupedProjects = groupProjectsByWorkspace(projects);
      const groupedWorkspaces = groupWorkspaces(workspaces);
      return Object.keys(groupedProjects).map((id) => ({
        label: groupedWorkspaces[Number(id)].name,
        options: groupedProjects[Number(id)].map((p) => ({
          label: p.displayName,
          value: p.id,
        })),
      }));
    },
    [togglProjects, workspaces]
  );

  const selected = useMemo(() => {
    const toggleProject =
      togglProjects.find((p) => p.id === mappedTogglProjectId) || null;
    return (
      toggleProject && {
        label: toggleProject?.displayName,
        value: toggleProject?.id,
      }
    );
  }, [togglProjects, mappedTogglProjectId]);

  function onSelect(option: { label: string; value: number } | null) {
    const value = option ? option.value : undefined;
    setIsUpdating(true);
    void onProjectSelected(value).finally(() => {
      setIsUpdating(false);
    });
  }

  return (
    <AsyncSelect<{ label: string; value: number }>
      isDisabled={isDisabled}
      defaultOptions={true}
      loadOptions={(
        inputValue: string,
        callback: (options: GroupType<OptionType>[]) => void
      ) => {
        callback(getFilteredOptions(inputValue));
      }}
      value={selected}
      isClearable
      onChange={onSelect}
      isLoading={isUpdating}
      placeholder="Select project"
    />
  );
}
